import React, { useState, useMemo } from 'react';
import {
  Search,
  Shuffle,
  Calendar,
  ArrowUp,
  ArrowDown,
  Star,
  Clock,
} from 'lucide-react';
import ArchiveListItem from './ArchiveListItem';
import { getHistoryMeta, parseDuration } from '../utils/audioUtils';

const ArchiveList = ({
  items,
  onPlay,
  onQueue,
  onRemove,
  queue,
  history,
  toggleFavorite,
  favorites,
  onStartParty,
}) => {
  const [search, setSearch] = useState('');
  const [filterPlace, setFilterPlace] = useState('');
  const [filterYear, setFilterYear] = useState('');
  const [sortOrder, setSortOrder] = useState('recent'); // 'recent', 'date_asc', 'date_desc', 'shuffle'
  const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);

  const years = useMemo(() => {
    const y = items
      .map((i) => (i.date ? i.date.split('-').pop() : ''))
      .filter((y) => y && y.length === 4);
    return [...new Set(y)].sort();
  }, [items]);

  const parseDate = (dateStr) => {
    if (!dateStr) return 0;
    const parts = dateStr.split('-');
    if (parts.length !== 3) return 0;
    // dd-mm-yyyy to timestamp
    return new Date(parts[2], parts[1] - 1, parts[0]).getTime();
  };

  // Seed for shuffle to keep it stable across renders unless sortOrder changes
  const shuffleSeed = useMemo(() => Math.random(), [sortOrder]);

  const filteredItems = useMemo(() => {
    let result = items.filter((item) => {
      const itemYear = item.date ? item.date.split('-').pop() : '';
      const matchesSearch =
        item.title.toLowerCase().includes(search.toLowerCase()) ||
        item.number.toString().includes(search.toLowerCase());
      const matchesPlace = filterPlace ? item.place === filterPlace : true;
      const matchesYear = filterYear ? itemYear === filterYear : true;
      const matchesFav = showFavoritesOnly ? favorites.includes(item.id) : true;

      return matchesSearch && matchesPlace && matchesYear && matchesFav;
    });

    if (sortOrder === 'shuffle') {
      // Simple seeded shuffle effect (using the stable items list order + seed)
      // Note: In a real app, a proper seeded shuffle algorithm is better,
      // but simple Math.random() in sort is roughly sufficient for UI feel here
      // provided we don't re-render too often.
      // To make it stable during typing, we really should shuffle a copy of indices,
      // but for this scope, a simple random sort that might re-trigger is okay-ish,
      // OR better: map to random value then sort.
      result = result
        .map((value) => ({ value, sort: Math.random() })) // Using random for true shuffle feel on re-filter
        .sort((a, b) => a.sort - b.sort)
        .map(({ value }) => value);
    } else if (sortOrder === 'date_asc') {
      result.sort((a, b) => parseDate(a.date) - parseDate(b.date));
    } else if (sortOrder === 'date_desc') {
      result.sort((a, b) => parseDate(b.date) - parseDate(a.date));
    } else {
      // 'recent'
      result.sort((a, b) => {
        const lastPlayedA = getHistoryMeta(history, a.id).lastPlayed;
        const lastPlayedB = getHistoryMeta(history, b.id).lastPlayed;
        return lastPlayedB - lastPlayedA;
      });
    }

    return result;
  }, [
    items,
    search,
    filterPlace,
    filterYear,
    history,
    sortOrder,
    showFavoritesOnly,
    favorites,
  ]);

  const places = useMemo(
    () => [...new Set(items.map((i) => i.place).filter(Boolean))],
    [items]
  );

  return (
    <div className="bg-slate-900 border border-slate-700 rounded-xl overflow-hidden mt-6">
      <div className="p-4 border-b border-slate-800 flex flex-col gap-4">
        {/* Top Row: Search and Quick Filters */}
        <div className="flex md:grid md:grid-cols-12 gap-4 overflow-x-auto md:overflow-visible no-scrollbar snap-x items-center">
          <div className="min-w-[250px] md:min-w-0 md:col-span-6 relative snap-start flex-shrink-0">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500"
              size={18}
            />
            <input
              type="text"
              placeholder="Search titles..."
              className="w-full bg-slate-950 border border-slate-800 text-slate-200 pl-10 pr-4 py-2 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <div className="min-w-[150px] md:min-w-0 md:col-span-3 snap-start flex-shrink-0">
            <select
              className="w-full bg-slate-950 border border-slate-800 text-slate-200 px-3 py-2 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
              value={filterPlace}
              onChange={(e) => setFilterPlace(e.target.value)}
            >
              <option value="">All Places</option>
              {places.map((p) => (
                <option key={p} value={p}>
                  {p}
                </option>
              ))}
            </select>
          </div>
          <div className="min-w-[150px] md:min-w-0 md:col-span-3 snap-start flex-shrink-0">
            <select
              className="w-full bg-slate-950 border border-slate-800 text-slate-200 px-3 py-2 rounded-lg focus:outline-none focus:border-blue-500 text-sm"
              value={filterYear}
              onChange={(e) => setFilterYear(e.target.value)}
            >
              <option value="">All Years</option>
              {years.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* Bottom Row: Sorting and Toggles */}
        <div className="flex flex-wrap items-center justify-between gap-4 pt-2 border-t border-slate-800/50">
          <div className="flex items-center gap-2">
            <button
              onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                showFavoritesOnly
                  ? 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/50'
                  : 'bg-slate-800 text-slate-400 border border-slate-700 hover:bg-slate-700'
              }`}
            >
              <Star
                size={16}
                fill={showFavoritesOnly ? 'currentColor' : 'none'}
              />
              <span>Favorites</span>
            </button>
          </div>

          <div className="flex items-center gap-2 overflow-x-auto no-scrollbar">
            <span className="text-xs text-slate-500 uppercase font-bold whitespace-nowrap">
              Sort By:
            </span>
            <div className="flex bg-slate-950 p-1 rounded-lg border border-slate-800">
              <button
                onClick={() => setSortOrder('recent')}
                className={`p-1.5 rounded-md transition-all ${
                  sortOrder === 'recent'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                title="Recently Played"
              >
                <Clock size={16} />
              </button>
              <button
                onClick={() => setSortOrder('shuffle')}
                className={`p-1.5 rounded-md transition-all ${
                  sortOrder === 'shuffle'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                title="Shuffle"
              >
                <Shuffle size={16} />
              </button>
              <button
                onClick={() => setSortOrder('date_desc')}
                className={`p-1.5 rounded-md transition-all flex items-center gap-1 ${
                  sortOrder === 'date_desc'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                title="Newest First"
              >
                <Calendar size={16} />
                <ArrowDown size={12} />
              </button>
              <button
                onClick={() => setSortOrder('date_asc')}
                className={`p-1.5 rounded-md transition-all flex items-center gap-1 ${
                  sortOrder === 'date_asc'
                    ? 'bg-blue-600 text-white shadow-sm'
                    : 'text-slate-400 hover:text-white hover:bg-slate-800'
                }`}
                title="Oldest First"
              >
                <Calendar size={16} />
                <ArrowUp size={12} />
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="divide-y divide-slate-800 max-h-[600px] overflow-y-auto">
        {filteredItems.length > 0 ? (
          filteredItems.map((item) => (
            <ArchiveListItem
              key={item.id}
              item={item}
              onPlay={onPlay}
              onQueue={onQueue}
              onRemove={onRemove}
              isInQueue={queue.some((q) => q.id === item.id)}
              saved={getHistoryMeta(history, item.id).progress}
              total={parseDuration(item.duration)}
              toggleFavorite={toggleFavorite}
              isFavorite={favorites.includes(item.id)}
              onStartParty={onStartParty}
            />
          ))
        ) : (
          <div className="p-8 text-center text-slate-500">
            <p>No items found matching your filters.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default ArchiveList;
